/**
 * `useDeleteCredential` — the one delete path for a service account.
 *
 * A credential owns bearer tokens (`ahoy_secret` rows attached through
 * `ahoy_secret_to_principal`) that the generic record delete knows nothing
 * about. Deleting the principal alone leaves those rows orphaned, so this hook
 * sweeps every attached secret FIRST (all pages, gathered before any delete so
 * pagination offsets never shift under it) and only then deletes the principal.
 *
 * Shared by the settings list row and the credential record slideout so both
 * surfaces delete the same way; a third caller must use this too.
 */

import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { deleteAttachedRows, deleteCredential } from './api-access-crud';

// Page size used when sweeping a credential's attached secrets for deletion.
const SECRETS_PAGE_SIZE = 100;

/** Mid-sentence noun for a credential ("Delete service account?"). */
export const CREDENTIAL_NOUN = 'service account';

export interface UseDeleteCredentialParams {
  orgId: number;
  credentialEntityId: string;
  entityTypeName: string;
  lastEventSeq: number;
  /**
   * Runs after the principal delete settles — success OR failure — because the
   * secrets are already gone by then and any list showing the credential must
   * refresh either way (mirrors the mint/add-secret onSettled pattern).
   */
  onDeleted: () => void;
}

export interface UseDeleteCredentialResult {
  isDeleting: boolean;
  /** Sweep the credential's secrets, then delete it. No-op while in flight. */
  deleteWithSecrets: () => Promise<void>;
}

export function useDeleteCredential({
  orgId,
  credentialEntityId,
  entityTypeName,
  lastEventSeq,
  onDeleted,
}: UseDeleteCredentialParams): UseDeleteCredentialResult {
  const { user } = useAuthInfo();
  // `user` is null until auth resolves; deriving the id here lets the callback
  // guard on it before any deref, avoiding a crash if delete is triggered early.
  const userId = user?.id as number | undefined;
  const [isDeleting, setIsDeleting] = useState(false);
  const credentialNoun = CREDENTIAL_NOUN;

  const deleteWithSecrets = useCallback(async () => {
    if (isDeleting || userId == null) {
      return;
    }
    setIsDeleting(true);
    try {
      try {
        const client = createCRMObjectClient(orgId, 'ahoy_secret');
        // Collect every attached row across all pages BEFORE deleting any, so a
        // credential with more than one page of rows isn't left with orphaned
        // rows. Gather first, then delete: deleting mid-pagination would shift
        // offsets and skip rows.
        const rowsToDelete: CrmObject[] = [];
        let page = 1;
        for (;;) {
          const listResult = await client.list({
            associated_with_entity_id: credentialEntityId,
            page,
            page_size: SECRETS_PAGE_SIZE,
            // Deletion only needs entity_id + last_event_seq (both top-level
            // fields, always returned) -- never the hint property. An empty
            // array tells the backend to skip property hydration entirely.
            properties: [],
          });
          rowsToDelete.push(...(listResult.results ?? []));
          if (!listResult.next) {
            break;
          }
          page += 1;
        }
        await deleteAttachedRows(
          orgId,
          userId,
          rowsToDelete.map(row => ({
            entityId: row.entity_id,
            principalEntityId: credentialEntityId,
            lastEventSeq: row.last_event_seq,
          }))
        );
      } catch (deleteErr) {
        logger.error(
          'Failed to fetch or delete associated secret during credential deletion',
          deleteErr as Error,
          {
            component: 'crm_events',
            org_id: orgId,
            credential_entity_id: credentialEntityId,
            correlation_id: (deleteErr as { correlationId?: string })
              ?.correlationId,
          }
        );
        toast.error(
          "Could not remove this credential's secrets. Please try again."
        );
        return;
      }
      // Secrets are already gone from the backend at this point. The list must
      // be invalidated whether the principal delete below succeeds or throws,
      // so onDeleted() runs unconditionally.
      try {
        await deleteCredential(
          orgId,
          userId,
          credentialEntityId,
          lastEventSeq,
          entityTypeName
        );
      } catch (err) {
        logger.error('Failed to delete credential', err as Error, {
          component: 'crm_events',
          org_id: orgId,
          credential_entity_id: credentialEntityId,
          secrets_swept: true,
        });
        toast.error(`We couldn't delete the ${credentialNoun}. Try again.`);
      } finally {
        onDeleted();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [
    isDeleting,
    orgId,
    userId,
    credentialEntityId,
    entityTypeName,
    credentialNoun,
    lastEventSeq,
    onDeleted,
  ]);

  return { isDeleting, deleteWithSecrets };
}
